import axios from "axios";
import { API_URL } from "../services/api-config";

/**
 * Shared payment-proof utilities used by both invoice-details and custom-table
 * (pre-navigation modal) to avoid code duplication.
 */

/** The 8 editable POP document fields used for change detection and counting. */
export const POP_EDITABLE_FIELDS = [
  "paymentProofRef",
  "paymentProofDate",
  "amountInPaymentProof",
  "currencyInPaymentProof",
  "beneficiaryName",
  "bankAccountNumber",
  "payerName",
  "taxIdNumber",
];

/** Empty POP document template. */
export const EMPTY_POP = {
  paymentProofRef: "",
  paymentProofDate: "",
  amountInPaymentProof: "",
  currencyInPaymentProof: "",
  beneficiaryName: "",
  bankAccountNumber: "",
  payerName: "",
  taxIdNumber: "",
  documenttype: "",
  proofdocumentpath: "",
  popid: null,
  expanded: false,
};

/**
 * Parse a raw date value into YYYY-MM-DD string.
 * Avoids timezone shift by extracting the date portion directly if possible.
 */
export const parsePaymentProofDate = (rawDate) => {
  if (!rawDate) return "";
  const datePartMatch = String(rawDate).match(/^(\d{4}-\d{2}-\d{2})/);
  if (datePartMatch) return datePartMatch[1];
  const parsed = new Date(rawDate);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().split("T")[0];
  return rawDate;
};

/**
 * Build a POP document object from API response data.
 */
export const buildPopFromApiData = (d) => ({
  paymentProofRef: d.payment_proof_reference_number || "",
  paymentProofDate: parsePaymentProofDate(
    d.payment_proof_date ||
      d.transactiondate ||
      d.transaction_date ||
      d.valuedate ||
      d.value_date
  ),
  amountInPaymentProof:
    d.amount_in_payment_proof != null
      ? String(d.amount_in_payment_proof)
      : "",
  currencyInPaymentProof: d.currency_in_payment_proof || "",
  beneficiaryName: d.beneficiary_name || "",
  bankAccountNumber: d.bank_account_number || "",
  payerName: d.payer_name || "",
  taxIdNumber: d.nit_number || "",
  documenttype: d.documenttype || d.document_type || "",
  proofdocumentpath: d.proofdocumentpath || d.proof_document_path || "",
  popid: d.popid || null,
  expanded: false,
});

/**
 * Validate BL Number(s): one or more comma-separated values, each exactly 9 alphanumeric characters.
 * Returns an error message string (empty string if valid).
 */
export const validateBlNumber = (value) => {
  if (!value) return "";
  const blNumbers = String(value)
    .split(",")
    .map((bl) => bl.trim())
    .filter(Boolean);
  const blNumRegex = /^[a-zA-Z0-9]{9}$/;
  const hasInvalidBl = blNumbers.some((bl) => !blNumRegex.test(bl));
  if (hasInvalidBl) {
    return "Each BL Number must be exactly 9 alphanumeric characters (letters and numbers only). For multiple BLs, use comma-separated values.";
  }
  return "";
};

/**
 * Count the number of filled POP editable fields (out of 8).
 */
export const getFilledPopFieldsCount = (pop) => {
  let count = 0;
  for (const field of POP_EDITABLE_FIELDS) {
    if (pop[field]) count++;
  }
  return count;
};

/**
 * Detect whether any payment form has changed (shared fields + POP fields).
 * @param {string} sharedBl - Current shared BL number
 * @param {string} originalBl - Original shared BL number
 * @param {string} sharedInvoice - Current shared invoice number
 * @param {string} originalInvoice - Original shared invoice number
 * @param {Array} popDocuments - Current POP documents array
 * @param {Array} originalPopDocuments - Original POP documents array
 */
export const isPaymentFormChanged = (
  sharedBl,
  originalBl,
  sharedInvoice,
  originalInvoice,
  popDocuments,
  originalPopDocuments
) => {
  if (sharedBl !== originalBl || sharedInvoice !== originalInvoice) return true;
  if (popDocuments.length !== originalPopDocuments.length) return true;
  return popDocuments.some((pop, i) => {
    const orig = originalPopDocuments[i] || {};
    return POP_EDITABLE_FIELDS.some((f) => (pop[f] || "") !== (orig[f] || ""));
  });
};

/**
 * Detect if a specific POP document's editable fields have changed.
 * @param {number} index - The index of the POP document
 * @param {Array} popDocuments - Current POP documents array
 * @param {Array} originalPopDocuments - Original POP documents array
 */
export const isPopDocChanged = (index, popDocuments, originalPopDocuments) => {
  const pop = popDocuments[index];
  const orig = originalPopDocuments[index] || {};
  return POP_EDITABLE_FIELDS.some((f) => (pop?.[f] || "") !== (orig?.[f] || ""));
};

/**
 * Check if at least one payment field is filled across shared fields + all POPs.
 */
export const isAnyPaymentFieldFilled = (sharedBl, sharedInvoice, popDocuments) => {
  if (sharedBl || sharedInvoice) return true;
  return popDocuments.some((pop) =>
    POP_EDITABLE_FIELDS.some((f) => !!pop[f])
  );
};

/**
 * Resolve action string to a status key for the status update API.
 */
export const resolveStatusKey = (action) => {
  if (!action) return null;
  const lower = action.toLowerCase();
  switch (lower) {
    case "approve":
      return "APPROVED";
    case "reject":
      return "REJECTED";
    case "non-compliant":
      return "NON-COMPLIANT";
    case "compliant":
      return "COMPLIANT";
    case "compliant-gcss":
      return "COMPLIANT";
    case "on-hold":
      return "ON-HOLD";
    case "exception":
      return "EXCEPTION";
    default:
      return action.toUpperCase();
  }
};

/**
 * Fetch payment proof data from the API and return parsed shared fields + POP documents.
 * Returns null if the case has no payment proof data (404).
 * Throws on all other errors.
 * @param {string} caseNumber
 * @param {string} authToken
 * @returns {{ sharedBl, sharedInvoice, popDocuments } | null}
 */
export const fetchPaymentProofData = async (caseNumber, authToken) => {
  const headers = authToken
    ? { Authorization: `Bearer ${authToken}` }
    : undefined;
  let response;
  try {
    response = await axios.get(
      `${API_URL}/payment_proof_details/${encodeURIComponent(caseNumber)}`,
      { headers }
    );
  } catch (err) {
    if (err.response && err.response.status === 404) {
      return null;
    }
    throw err;
  }
  const data = response.data?.body || {};

  const sharedBl = data.bl_number || "";    
  const sharedInvoice = data.invoicenumber || "";

  const popDocs = data.pop_documents;
  const docs =
    Array.isArray(popDocs) && popDocs.length > 0
      ? popDocs.map((d) => buildPopFromApiData(d))
      : [buildPopFromApiData(data)];

  return { sharedBl, sharedInvoice, popDocuments: docs };
};

/**
 * Build payment proof update payload for a single POP document.
 */
export const buildPaymentProofPayload = ({
  caseNumber,
  pop,
  popid,
  sharedBl,
  sharedInvoice,
  modifiedBy,
}) => ({
  casenumber: caseNumber,
  documenttype: pop.documenttype || "POP",
  popid: popid || null,
  bl_number: sharedBl || null,
  invoice_number: sharedInvoice || null,
  payment_proof_ref: pop.paymentProofRef || null,
  payment_proof_date: pop.paymentProofDate || null,
  amount_in_payment_proof: pop.amountInPaymentProof
    ? parseFloat(pop.amountInPaymentProof)
    : null,
  currency_in_payment_proof: pop.currencyInPaymentProof || null,
  beneficiary_name: pop.beneficiaryName || null,
  bank_account_number: pop.bankAccountNumber || null,
  payer_name: pop.payerName || null,
  tax_id_number: pop.taxIdNumber || null,
  modifiedBy: modifiedBy,
  modifieddatetime: new Date().toISOString(),
});

/**
 * Fetch fresh popid for a given POP document index from the API.
 * Returns popid or null.
 */
export const fetchFreshPopId = async (caseNumber, popIndex, authToken) => {
  const headers = authToken
    ? { Authorization: `Bearer ${authToken}` }
    : undefined;
  try {
    const response = await axios.get(
      `${API_URL}/payment_proof_details/${encodeURIComponent(caseNumber)}`,
      { headers }
    );
    const freshData = response.data?.body || {};
    const freshDocs = freshData.pop_documents;
    if (Array.isArray(freshDocs) && freshDocs[popIndex]) {
      return freshDocs[popIndex].popid || null;
    }
    return freshData.popid || null;
  } catch (err) {
    console.debug(
      `Could not fetch fresh popid for POP ${popIndex + 1}:`,
      err?.message || err
    );
    return null;
  }
};
